import { Button } from "@/components/ui/baseUi/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/baseUi/carousel"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/baseUi/command"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { removeDiacritics } from "@/modules/utils/stringUtils"
import OnboardingPaymentDot from "@images/payment/onboardingPaymentDot.svg"
import OnboardingPaymentFlag from "@images/payment/onboardingPaymentFlag.svg"
import StripeLogo from "@images/payment/stripe-logo.svg"
import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { useStripeAccount, useStripeAvailableCountries } from "@modules/finances/hooks/useStripeAccount"
import { StripeCountryCode } from "@modules/payment/stripeTypes"
import { CheckIcon, ChevronsUpDownIcon, LoaderCircle } from "lucide-react"
import { useImperativeHandle, useState } from "react"
import { Link } from "react-router"
import styled from "styled-components"

export type OnBoardingStripeModalRef = {
  show: () => void
}

export function OnBoardingStripeModal({ ref }: { ref: React.Ref<OnBoardingStripeModalRef> }) {
  const [visible, showPopup] = useState(false)

  useImperativeHandle(ref, () => {
    return {
      show: () => showPopup(true),
    }
  }, [])

  const cards = [
    { index: 0, children: <OnBoardingCard1 /> },
    { index: 1, children: <OnBoardingCard2 /> },
    { index: 2, children: <OnBoardingCard3 /> },
    { index: 3, children: <OnBoardingCard4 /> },
  ]

  return (
    <Dialog open={visible} onOpenChange={() => showPopup(false)}>
      <DialogContent
        className="flex w-lg"
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Carousel className="mx-auto w-8/10">
          <CarouselContent>
            {cards.map((card, index) => (
              <CarouselItem key={index}>{card.children}</CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </DialogContent>
    </Dialog>
  )
}

const OnBoardingCard1: React.FC = () => {
  return (
    <div className="flex h-full flex-col items-center justify-between gap-2">
      <DialogHeader>
        <DialogTitle>
          <Text fontStyle="TL" className="mb-4 text-center">
            {word("program.stripe.onboarding.title")}
          </Text>
        </DialogTitle>

        <DialogDescription>
          <Text fontStyle="BM" className="mb-4 text-center">
            {word("program.stripe.onboarding.introduction")}
          </Text>
        </DialogDescription>
      </DialogHeader>

      <img src={OnboardingPaymentDot} width={250} height={250} />

      <div className="mt-4 flex flex-row items-center justify-between rounded-2xl bg-primary-500 px-4 py-2">
        <WarningIcon color="currentColor" width={20} height={20} className="text-textError-body" />
        <Text fontStyle="BS" className="ml-2 text-center">
          {word("program.stripe.onboarding.paymentDayInformation")}
        </Text>
      </div>
    </div>
  )
}

const OnBoardingCard2: React.FC = () => {
  return (
    <div className="flex h-full flex-col items-center justify-between gap-2">
      <DialogHeader>
        <DialogTitle>
          <Text fontStyle="TL" className="text-center">
            {word("program.stripe.onboarding.title")}
          </Text>
        </DialogTitle>

        <DialogDescription>
          <Text fontStyle="BM" className="text-center wrap-break-word whitespace-pre-line">
            {word("program.stripe.onboarding.partnerAssociation")}
          </Text>
        </DialogDescription>
      </DialogHeader>

      <img src={StripeLogo} width={260} />

      <Text fontStyle="TS" className="mb-8 text-center">
        {word("program.stripe.onboarding.partnerStripe")}
      </Text>
    </div>
  )
}

const OnBoardingCard3: React.FC = () => {
  return (
    <div className="flex h-full flex-col items-center justify-between gap-2">
      <DialogHeader>
        <DialogTitle>
          <Text fontStyle="TL" className="mb-4 text-center">
            {word("program.stripe.onboarding.title")}
          </Text>
        </DialogTitle>
      </DialogHeader>

      <div className="flex flex-col items-center gap-1 rounded-2xl bg-neutral-100 p-4">
        <Check>
          <CheckmarkIcon color="currentColor" size={16} />
        </Check>
        <Text fontStyle="LL" className="mb-2 text-center">
          {word("program.stripe.onboarding.requiredInformation.title")}
        </Text>
        <Text fontStyle="BS" className="text-center">
          {word("program.stripe.onboarding.requiredInformation.name")}
        </Text>
        <Text fontStyle="BS" className="text-center">
          {word("program.stripe.onboarding.requiredInformation.birthday")}
        </Text>
        <Text fontStyle="BS" className="text-center">
          {word("program.stripe.onboarding.requiredInformation.email")}
        </Text>
        <Text fontStyle="BS" className="text-center">
          {word("program.stripe.onboarding.requiredInformation.phoneNumber")}
        </Text>
        <Text fontStyle="BS" className="text-center">
          {word("program.stripe.onboarding.requiredInformation.address")}
        </Text>
        <Text fontStyle="BS" className="text-center">
          {word("program.stripe.onboarding.requiredInformation.bankData")}
        </Text>
        <Text fontStyle="BS" className="text-center">
          {word("program.stripe.onboarding.requiredInformation.countryRelatedChanges")}
        </Text>
      </div>

      <div className="flex-rowmy-4 flex items-center justify-between rounded-2xl bg-primary-500 px-4 py-3">
        <WarningIcon color="currentColor" width={20} height={20} className="shrink-0" />
        <Text fontStyle="LS" className="ml-2 px-2">
          {word("program.stripe.onboarding.requiredInformation.seriousNotice")}
        </Text>
      </div>
    </div>
  )
}

const OnBoardingCard4: React.FC = () => {
  const [selectedCountry, setSelectedCountry] = useState<StripeCountryCode | undefined>()
  const { createStripeAccount, loading } = useStripeAccount()
  const [openDropdown, setOpenDropdown] = useState(false)

  const { availableStripeCountries } = useStripeAvailableCountries()

  const countryOptions = availableStripeCountries
    .map((stripePlatform) =>
      stripePlatform.countries.map((country) => ({
        value: country,
        label: word(`global.country.${country}`),
        stripePlatform: stripePlatform.stripePlatform,
      })),
    )
    .flat()
    .sort((a, b) => {
      return a.label.localeCompare(b.label)
    })

  const handleCreateStripeAccount = () => {
    if (!selectedCountry) return
    const selectedStripePlatform = countryOptions.find((country) => country.value === selectedCountry)?.stripePlatform
    if (!selectedStripePlatform) return
    createStripeAccount(selectedCountry, selectedStripePlatform)
  }

  return (
    <div className="flex h-full flex-col items-center justify-between gap-2">
      <DialogHeader>
        <DialogTitle>
          <Text fontStyle="TL" className="mb-2 text-center">
            {word("program.stripe.onboarding.title")}
          </Text>
        </DialogTitle>
      </DialogHeader>

      <img src={OnboardingPaymentFlag} width={200} height={200} />

      <Text fontStyle="BM" className="mt-2 text-center">
        {word("program.stripe.onboarding.countryHost")}
      </Text>

      <div className="flex w-full flex-col gap-2 px-4">
        <Text className="text-textDefault-caption" fontStyle="LS">
          {word("global.country.label").toUpperCase()}
          <Text fontStyle="LL" className="text-red-500">
            {" "}
            *
          </Text>
        </Text>

        <Popover open={openDropdown} onOpenChange={setOpenDropdown}>
          <PopoverTrigger
            render={
              <Button variant="outline" role="combobox" aria-expanded={openDropdown} className="w-full justify-between">
                {selectedCountry
                  ? countryOptions.find((countryOptions) => countryOptions.value === selectedCountry)?.label
                  : word("global.selectCountry")}

                <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            }
          />
          <PopoverContent className="w-65 border-0 p-0">
            <Command
              className="**:data-[slot=command-input-wrapper]:bg-neutral-50"
              filter={(value, search) => {
                if (removeDiacritics(value.toLowerCase()).includes(removeDiacritics(search.toLowerCase()))) return 1
                return 0
              }}
            >
              <CommandInput placeholder={word("global.searchCountry")} />
              <CommandList className="bg-surface-background">
                <CommandEmpty>{word("global.searchCountryNotFound")}</CommandEmpty>
                <CommandGroup>
                  {countryOptions.map((countryOption) => (
                    <CommandItem
                      key={countryOption.value}
                      value={countryOption.label}
                      onSelect={(currentValue) => {
                        const foundCountry = countryOptions.find((item) => item.label === currentValue)
                        setSelectedCountry(foundCountry?.value as StripeCountryCode)
                        setOpenDropdown(false)
                      }}
                      className="cursor-pointer hover:bg-neutral-200"
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedCountry === countryOption.value ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {countryOption.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <Button variant="underline" size="sm" className="mb-4 self-end">
        <Link to="mailto:contact@hustleup-app.com?subject=I%20can't%20find%20my%20country%20&body=">
          {word("program.stripe.onboarding.countryNotFound")}
        </Link>
      </Button>

      <Button
        variant="primary"
        size="lg"
        disabled={!selectedCountry || loading}
        onClick={() => {
          handleCreateStripeAccount()
        }}
        className="mb-3 w-full"
        isLoading={loading}
      >
        {word("program.stripe.onboarding.start")}
        {loading && <LoaderCircle className="animate-spin" />}
      </Button>
    </div>
  )
}

const Check = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--surface-background);
  width: 30px;
  height: 30px;
  border-radius: 15px;
  margin-bottom: 10px;
`
